import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Home, AlertCircle, Dumbbell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAnalytics } from "@/hooks/useAnalytics";
import { ROUTES } from "@/constants/routes";

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { trackNotFound } = useAnalytics();
  // trackNotFound gets a new identity on every render of useAnalytics,
  // which re-ran this effect (and the analytics event) three times per
  // visit. Track each path once.
  const trackedPath = useRef<string | null>(null);

  useEffect(() => {
    if (trackedPath.current === location.pathname) return;
    trackedPath.current = location.pathname;
    // Log analytics event for 404 with enhanced data
    trackNotFound(location.pathname, document.referrer || 'direct');

    if (import.meta.env.DEV) {
      console.error("404 Error: User attempted to access non-existent route:", location.pathname);
    }
  }, [location.pathname, trackNotFound]);

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-surface p-4"
      data-testid="not-found-page"
    >
      <Card className="max-w-md w-full p-8 text-center">
        {/* Icon */}
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-error/10 grid place-items-center">
          <AlertCircle className="w-8 h-8 text-destructive" />
        </div>

        {/* Title */}
        <h1 className="text-4xl font-bold text-text mb-2">404</h1>
        <p className="text-xl text-muted mb-4">{t("notFound.title")}</p>

        {/* Path info */}
        <div className="bg-muted-bg rounded-lg p-3 mb-6">
          <p className="text-sm text-muted break-all">
            <Trans
              i18nKey="notFound.description"
              values={{ path: location.pathname }}
              components={{ code: <code className="font-mono text-text" /> }}
            />
          </p>
        </div>

        {/* Navigation buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={() => navigate(ROUTES.DASHBOARD)}
            className="flex-1"
            data-testid="go-to-dashboard"
          >
            <Home className="w-4 h-4 mr-2" />
            {t("notFound.go_dashboard")}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(ROUTES.EXERCISES)}
            className="flex-1"
            data-testid="go-to-exercises"
          >
            <Dumbbell className="w-4 h-4 mr-2" />
            {t("notFound.go_exercises")}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default NotFound;
